import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit, FiTrash2, FiUser, FiMail, FiPhone, FiAlertCircle, FiClock, FiDollarSign, FiFileText } = FiIcons;

const AgencyCard = ({ agency, onEdit, onDelete, articleStats }) => {
  const isLowStock = agency.payment_type === 'prepaid' && agency.remaining_articles <= 10;
  const stockPercentage = agency.payment_type === 'prepaid' 
    ? (agency.remaining_articles / agency.total_articles) * 100 
    : 0;

  const getBillingStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-success-600 bg-success-100';
      case 'pending': return 'text-warning-600 bg-warning-100';
      case 'overdue': return 'text-danger-600 bg-danger-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{agency.name}</h3>
          <p className="text-sm text-gray-600">{agency.niche_type}</p>
          <div className="flex items-center mt-2">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              agency.payment_type === 'prepaid' 
                ? 'bg-blue-100 text-blue-800' 
                : 'bg-purple-100 text-purple-800'
            }`}>
              {agency.payment_type === 'prepaid' ? 'Prepaid' : 'Post-Payment'}
            </span>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => onEdit(agency)}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiEdit} className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(agency)}
            className="p-2 text-gray-500 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <SafeIcon icon={FiUser} className="w-4 h-4 mr-2" />
          {agency.contact_person}
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <SafeIcon icon={FiMail} className="w-4 h-4 mr-2" />
          {agency.email}
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2" />
          {agency.phone}
        </div>
      </div>

      <div className="border-t pt-4">
        {agency.payment_type === 'prepaid' ? (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Articles Remaining</span>
              {isLowStock && (
                <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-warning-500" />
              )}
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl font-bold text-gray-900">{agency.remaining_articles}</span>
              <span className="text-sm text-gray-500">/ {agency.total_articles}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  stockPercentage > 25 ? 'bg-success-500' : 
                  stockPercentage > 10 ? 'bg-warning-500' : 'bg-danger-500'
                }`}
                style={{ width: `${Math.max(stockPercentage, 5)}%` }}
              />
            </div>
            <div className="flex justify-between text-sm text-gray-600 mt-2">
              <span>Total Price: {agency.total_price} LEI</span>
              <span>Per Article: {agency.price_per_article} LEI</span>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <SafeIcon icon={FiFileText} className="w-4 h-4 text-primary-600" />
                  <span className="text-sm text-gray-600">Published</span>
                </div>
                <p className="text-2xl font-bold text-primary-600">{articleStats?.published || 0}</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-success-600" />
                  <span className="text-sm text-gray-600">To Bill</span>
                </div>
                <p className="text-2xl font-bold text-success-600">
                  {((articleStats?.published || 0) * agency.price_per_article).toFixed(2)} LEI
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Billing Status</span>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getBillingStatusColor(agency.billing_status)}`}>
                {agency.billing_status?.toUpperCase() || 'PENDING'}
              </span>
            </div>
            
            <div className="text-sm text-gray-600 mt-2">
              <div className="flex justify-between">
                <span>Rate: {agency.price_per_article} LEI/article</span>
                <span>Total Articles: {articleStats?.total || 0}</span>
              </div>
              {agency.last_billed_at && (
                <div className="text-xs text-gray-500 mt-1">
                  Last billed: {new Date(agency.last_billed_at).toLocaleDateString()}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default AgencyCard;